export type IfcProperty = {
  name: string;
  value: string;
};

export type IfcSelectionProperties = {
  modelId: string;
  localId: number;
  properties: IfcProperty[];
};

export type IfcCategoryFilter = {
  category: string;
  label: string;
  count: number;
  visible: boolean;
  color: string;
  opacity: number;
};

type IfcPropertyPanelProps = {
  selection: IfcSelectionProperties | null;
  loading?: boolean;
  categoryFilters?: IfcCategoryFilter[];
  categoryBusy?: string | null;
  categoryStatus?: string | null;
  onCategoryVisibleChange?: (category: string, visible: boolean) => void;
  onCategoryAppearanceChange?: (category: string, appearance: Partial<Pick<IfcCategoryFilter, "color" | "opacity">>) => void;
  onShowAllCategories?: () => void;
};

const COMMON_IFC_RENDER_COLORS = [
  { label: "Red", value: "#c2410c" },
  { label: "Yellow", value: "#d6b55c" },
  { label: "Dark grey", value: "#676c70" },
  { label: "Light grey", value: "#c6cbd1" },
  { label: "Blue", value: "#6fbfcb" },
  { label: "White", value: "#f8fafc" },
];

export default function IfcPropertyPanel({
  selection,
  loading = false,
  categoryFilters = [],
  categoryBusy = null,
  categoryStatus = null,
  onCategoryVisibleChange,
  onCategoryAppearanceChange,
  onShowAllCategories,
}: IfcPropertyPanelProps) {
  return (
    <aside className="ifc-property-panel" aria-label="Selected element properties">
      {categoryFilters.length > 0 && (
        <section className="ifc-category-panel" aria-label="IFC category filters">
          <div className="ifc-category-panel__header">
            <div>
              <p className="ifc-property-panel__eyebrow">Filters</p>
              <h2>Categories</h2>
            </div>
            <button type="button" onClick={onShowAllCategories} disabled={categoryBusy !== null}>
              Show all
            </button>
          </div>
          <div className="ifc-category-panel__list">
            {categoryFilters.map((filter) => (
              <div key={filter.category} className="ifc-category-filter" title={filter.category}>
                <input
                  type="checkbox"
                  aria-label={`Show ${filter.label}`}
                  checked={filter.visible}
                  disabled={categoryBusy !== null}
                  onChange={(event) => onCategoryVisibleChange?.(filter.category, event.target.checked)}
                />
                <span className="ifc-category-filter__name">{filter.label}</span>
                <span className="ifc-category-filter__count">{filter.count}</span>
                <div className="ifc-category-filter__palette" role="group" aria-label={`${filter.label} colour`}>
                  {COMMON_IFC_RENDER_COLORS.map((option) => {
                    const selected = option.value.toLowerCase() === filter.color.toLowerCase();
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={selected ? "ifc-category-filter__swatch ifc-category-filter__swatch--selected" : "ifc-category-filter__swatch"}
                        aria-label={`${filter.label} ${option.label}`}
                        aria-pressed={selected}
                        title={option.label}
                        disabled={categoryBusy !== null}
                        onClick={() => onCategoryAppearanceChange?.(filter.category, { color: option.value })}
                      >
                        <span style={{ backgroundColor: option.value }} />
                      </button>
                    );
                  })}
                </div>
                <label className="ifc-category-filter__opacity">
                  <span>Opacity</span>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={Math.round(filter.opacity * 100)}
                    disabled={categoryBusy !== null}
                    onChange={(event) => onCategoryAppearanceChange?.(filter.category, { opacity: Number(event.target.value) / 100 })}
                  />
                  <strong>{Math.round(filter.opacity * 100)}%</strong>
                </label>
              </div>
            ))}
          </div>
          {categoryStatus && <p className="ifc-category-panel__status">{categoryStatus}</p>}
        </section>
      )}

      <div className="ifc-property-panel__header">
        <div>
          <p className="ifc-property-panel__eyebrow">Selection</p>
          <h2>Element Properties</h2>
        </div>
      </div>

      {loading ? (
        <div className="ifc-property-panel__empty">Reading element properties...</div>
      ) : selection ? (
        <div className="ifc-property-panel__content">
          <div className="ifc-property-panel__ids">
            <span>Model</span>
            <strong>{selection.modelId}</strong>
            <span>Local ID</span>
            <strong>{selection.localId}</strong>
          </div>
          <dl className="ifc-property-list">
            {selection.properties.length > 0 ? selection.properties.map((property) => (
              <div key={`${property.name}:${property.value}`} className="ifc-property-list__row">
                <dt>{property.name}</dt>
                <dd>{property.value}</dd>
              </div>
            )) : (
              <div className="ifc-property-panel__empty">No readable properties were returned for this element.</div>
            )}
          </dl>
        </div>
      ) : (
        <div className="ifc-property-panel__empty">Select an element in the model to inspect its properties.</div>
      )}
    </aside>
  );
}
